import { useTranslation } from "react-i18next";
import {
  Plus, Trash2, Save, Share2, ChevronLeft, ChevronRight, ArrowLeft,
  List, CalendarDays, FileText,
} from "lucide-react";
import { usePatientNotes } from "../../application/usePatientNotes";
import NotionEditor from "../../../../shared/ui/form/input/NotionEditor";
import Button from "../../../../shared/ui/button/Button";
import NoteRepliesSection from "../../../../shared/ui/notes/NoteRepliesSection";
import type { PatientNote } from "../../domain/types";
import { noteToDateKey } from "../../domain/patientNotes";
import { toLocalDateKey } from "../../../../shared/utils/date";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getPlainText(body: string): string {
  try {
    const blocks = JSON.parse(body);
    if (Array.isArray(blocks)) {
      for (const block of blocks) {
        const texts: string[] = [];
        if (Array.isArray(block.content)) {
          for (const item of block.content) {
            if (item.type === "text" && item.text) texts.push(item.text);
          }
        }
        const line = texts.join("").trim();
        if (line) return line;
      }
      return "";
    }
  } catch { /* fall through */ }
  return body.replace(/<[^>]*>/g, "").trim();
}

function getNoteTitle(note: PatientNote): string {
  if (note.title?.trim()) return note.title.trim();
  return getPlainText(note.body).split(/\n/)[0].trim().slice(0, 60);
}

function buildMonthCells(year: number, month: number): (number | null)[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startOffset = (first.getDay() + 6) % 7; // Mon = 0
  const cells: (number | null)[] = Array(startOffset).fill(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// ── CalendarPanel ─────────────────────────────────────────────────────────────

function CalendarPanel({
  allNotes,
  loading,
  selectedDay,
  onSelectDay,
  currentMonth,
  onMonthChange,
}: {
  allNotes: PatientNote[];
  loading: boolean;
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
  currentMonth: Date;
  onMonthChange: (d: Date) => void;
}) {
  const { i18n } = useTranslation();
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const cells = buildMonthCells(year, month);
  const today = toLocalDateKey();

  const notesByDate: Record<string, number> = {};
  allNotes.forEach((n) => {
    const key = noteToDateKey(n);
    notesByDate[key] = (notesByDate[key] ?? 0) + 1;
  });

  // Locale-aware Mon-first weekday headers
  const weekHeaders = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 1 + i); // Jan 1 2024 = Monday
    return d.toLocaleDateString(i18n.language, { weekday: "short" });
  });

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Month navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
        <button
          onClick={() => onMonthChange(new Date(year, month - 1))}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 capitalize">
          {currentMonth.toLocaleDateString(i18n.language, { month: "long", year: "numeric" })}
        </span>
        <button
          onClick={() => onMonthChange(new Date(year, month + 1))}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 px-3 pt-3 pb-1 shrink-0">
        {weekHeaders.map((h) => (
          <div key={h} className="text-center text-xs text-content-tertiary font-medium py-1 capitalize">
            {h}
          </div>
        ))}
      </div>

      {/* Day grid */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 rounded-full border-2 border-brand-400 border-t-transparent animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-y-0.5 px-3 pb-3">
          {cells.map((day, i) => {
            if (!day) return <div key={`pad-${i}`} className="h-10" />;
            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const count = notesByDate[dateStr] ?? 0;
            const isToday = dateStr === today;
            const isSelected = dateStr === selectedDay;
            return (
              <button
                key={dateStr}
                onClick={() => onSelectDay(dateStr)}
                className={`relative flex flex-col items-center justify-center h-10 rounded-xl text-sm transition-colors
                  ${isSelected
                    ? "bg-brand-500 text-white font-semibold"
                    : isToday
                    ? "ring-2 ring-brand-400 ring-inset text-brand-600 dark:text-brand-400 font-semibold"
                    : "hover:bg-gray-100 dark:hover:bg-white/5 text-content-secondary"
                  }`}
              >
                {day}
                {count > 0 && (
                  <span
                    className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${
                      isSelected ? "bg-white/80" : "bg-brand-400"
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PatientNotesPage() {
  const { t, i18n } = useTranslation();
  const {
    notes, page, totalPages, loading,
    selection, setSelection,
    editorBody, setEditorBody, editorTitle, setEditorTitle,
    shared, setShared, noteDate, setNoteDate,
    saving, deleting, showDeleteConfirm, setShowDeleteConfirm,
    mobileView, setMobileView,
    viewMode, allNotes, allNotesLoading,
    selectedDay, setSelectedDay, currentMonth, setCurrentMonth,
    replies, repliesLoading, replySubmitting,
    loadPage, handleSubmitReply, switchView, selectNote, selectNew, handleSave, handleDelete,
  } = usePatientNotes();

  // ── Derived ─────────────────────────────────────────────────────────────────

  const editorKey =
    selection?.type === "new" ? "new"
    : selection?.type === "existing" ? selection.note.id
    : "none";

  const dayNotes = selectedDay
    ? allNotes.filter((n) => noteToDateKey(n) === selectedDay)
    : [];

  // ── Editor panel (shared) ───────────────────────────────────────────────────

  const renderEditor = () => (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => {
              if (viewMode === "calendar") setSelection(null);
              else setMobileView("list");
            }}
            className="xl:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <p className="text-xs text-content-tertiary truncate">
            {selection?.type === "new"
              ? t("portal.notes.newNote")
              : selection?.type === "existing"
              ? new Date(selection.note.created_at).toLocaleString()
              : ""}
          </p>
          {selection?.type === "existing" && selection.note.shared_with_therapist && (
            selection.note.read_by_therapist_at
              ? <span className="shrink-0 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">{t("portal.notes.seen")}</span>
              : <span className="shrink-0 text-xs font-medium text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 px-2 py-0.5 rounded-full">{t("portal.notes.notSeen")}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selection?.type === "existing" && !showDeleteConfirm && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              aria-label={t("common.delete", "Delete")}
              className="text-red-500 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
          {showDeleteConfirm && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600">{t("portal.notes.deleteConfirm")}</span>
              <Button size="sm" variant="destructive" onClick={handleDelete} loading={deleting}>
                {t("common.yes")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                {t("common.cancel")}
              </Button>
            </div>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            loading={saving}
            startIcon={<Save className="w-3.5 h-3.5" />}
          >
            {t("common.save")}
          </Button>
        </div>
      </div>

      {/* Two-column layout: Col 1 = (title/date/share + editor), Col 2 = comments */}
      <div className="flex flex-1 min-h-0 flex-col lg:flex-row">

        {/* Column 1 */}
        <div className="flex flex-col flex-1 min-h-0 min-w-0">
          {/* Row 1: Title + date + share */}
          <div className="px-6 pt-5 pb-3 flex flex-col gap-3 shrink-0">
            <input
              type="text"
              value={editorTitle}
              onChange={(e) => setEditorTitle(e.target.value)}
              placeholder={t("portal.notes.titlePlaceholder")}
              maxLength={200}
              className="w-full text-xl font-semibold bg-transparent border-none outline-none text-content-primary placeholder-gray-300 dark:placeholder-gray-600"
            />
            {viewMode === "calendar" && (
              <input
                type="date"
                value={noteDate}
                onChange={(e) => setNoteDate(e.target.value)}
                className="w-36 text-xs text-content-tertiary bg-transparent border border-gray-200 dark:border-white/10 rounded-lg px-2 py-1 outline-none focus:border-brand-400 transition-colors"
              />
            )}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <div className="relative mt-0.5 shrink-0">
                <input
                  type="checkbox"
                  checked={shared}
                  onChange={(e) => setShared(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 rounded-full bg-gray-200 dark:bg-gray-700 peer-checked:bg-brand-500 transition-colors" />
                <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-content-secondary">
                  {t("portal.notes.shareToggle")}
                </p>
                <p className="text-xs text-content-tertiary mt-0.5">{t("portal.notes.shareHint")}</p>
              </div>
            </label>
            <div className="border-t border-gray-100 dark:border-white/5" />
          </div>

          {/* Row 2: Note editor */}
          <div className="flex-1 overflow-y-auto px-6 pb-4 min-w-0">
            <NotionEditor
              key={editorKey}
              value={editorBody}
              onChange={(html) => setEditorBody(html)}
              className="min-h-[200px]"
              placeholder={t("portal.notes.placeholder")}
            />
          </div>

          {/* Mobile: replies below editor */}
          {selection?.type === "existing" && selection.note.shared_with_therapist && (
            <div className="lg:hidden">
              <NoteRepliesSection
                replies={replies}
                loading={repliesLoading}
                submitting={replySubmitting}
                onSubmit={handleSubmitReply}
                currentAuthorType="patient"
                variant="bottom"
              />
            </div>
          )}
        </div>

        {/* Column 2: Comments — desktop only, shared existing notes */}
        {selection?.type === "existing" && selection.note.shared_with_therapist && (
          <div className="hidden lg:flex flex-col w-72 xl:w-80 shrink-0">
            <NoteRepliesSection
              replies={replies}
              loading={repliesLoading}
              submitting={replySubmitting}
              onSubmit={handleSubmitReply}
              currentAuthorType="patient"
              variant="side"
            />
          </div>
        )}

      </div>
    </>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">
            {t("portal.notes.title")}
          </h1>
          <p className="text-sm text-content-tertiary mt-1">
            {t("portal.notes.subtitle")}
          </p>
        </div>
        {/* View toggle */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-white/10 rounded-xl p-1 shrink-0">
          <button
            onClick={() => switchView("list")}
            title={t("portal.notes.listView")}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === "list"
                ? "bg-white dark:bg-white/10 text-brand-600 shadow-sm"
                : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
            }`}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => switchView("calendar")}
            title={t("portal.notes.calendarView")}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === "calendar"
                ? "bg-white dark:bg-white/10 text-brand-600 shadow-sm"
                : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
            }`}
          >
            <CalendarDays className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content card */}
      <div className="glass-effect glass-border border rounded-2xl shadow-sm overflow-hidden flex xl:h-[calc(100vh-16rem)]">

        {viewMode === "list" ? (
          <>
            {/* ── LIST VIEW ────────────────────────────────────────────── */}

            {/* Left: note list */}
            <div className={`${mobileView === "editor" ? "hidden" : "flex"} xl:flex w-full xl:w-72 shrink-0 flex-col border-r border-gray-200 dark:border-white/10`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
                <span className="text-sm font-semibold text-content-secondary">
                  {t("portal.notes.new")}
                </span>
                <button
                  onClick={selectNew}
                  title={t("portal.notes.new")}
                  className="p-1.5 rounded-lg text-brand-600 hover:bg-brand-50 dark:hover:bg-white/5 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
                {selection?.type === "new" && (
                  <li className="px-4 py-3 bg-brand-50 dark:bg-brand-900/20 border-l-2 border-brand-500">
                    <p className="text-sm font-medium text-brand-700 dark:text-brand-300">
                      {t("portal.notes.newNote")}
                    </p>
                  </li>
                )}
                {loading ? (
                  <li className="px-4 py-8 text-center text-sm text-content-tertiary">{t("common.loading")}</li>
                ) : notes.length === 0 ? (
                  <li className="px-4 py-8 text-center text-sm text-content-tertiary italic">{t("portal.notes.empty")}</li>
                ) : (
                  notes.map((note) => {
                    const isSelected = selection?.type === "existing" && selection.note.id === note.id;
                    const title = getNoteTitle(note) || t("portal.notes.untitled");
                    return (
                      <li key={note.id}>
                        <button
                          onClick={() => selectNote(note)}
                          className={`w-full text-left px-4 py-3 transition-colors border-l-2 ${
                            isSelected
                              ? "bg-brand-50 dark:bg-brand-900/20 border-brand-500"
                              : "hover:bg-gray-50 dark:hover:bg-white/5 border-transparent"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            {note.shared_with_therapist && (
                              <Share2 className="w-3 h-3 text-brand-400 shrink-0" />
                            )}
                            <p className={`text-sm font-medium truncate ${isSelected ? "text-brand-700 dark:text-brand-300" : "text-gray-800 dark:text-gray-200"}`}>
                              {title}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-1 gap-2">
                            <span className="text-xs text-content-tertiary">
                              {new Date(noteToDateKey(note) + "T12:00:00").toLocaleDateString(i18n.language)}
                            </span>
                            {note.shared_with_therapist && (
                              note.read_by_therapist_at
                                ? <span className="text-xs text-green-600 dark:text-green-400 font-medium">{t("portal.notes.seen")}</span>
                                : <span className="text-xs text-brand-500 font-medium">{t("portal.notes.notSeen")}</span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              {totalPages > 1 && (
                <div className="shrink-0 flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-white/10">
                  <button
                    onClick={() => loadPage(page - 1)}
                    disabled={page <= 1 || loading}
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-content-tertiary">{page} / {totalPages}</span>
                  <button
                    onClick={() => loadPage(page + 1)}
                    disabled={page >= totalPages || loading}
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-30 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Right: editor */}
            <div className={`${mobileView === "list" ? "hidden" : "flex"} xl:flex flex-1 flex-col overflow-hidden`}>
              {selection ? renderEditor() : (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-content-tertiary py-16">
                  <p className="text-sm">{t("portal.notes.selectOrCreate")}</p>
                  <Button size="sm" variant="outline" onClick={selectNew} startIcon={<Plus className="w-3.5 h-3.5" />}>
                    {t("portal.notes.new")}
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* ── CALENDAR VIEW ─────────────────────────────────────────── */}

            {/* Left: calendar */}
            <div className={`${mobileView === "editor" ? "hidden" : "flex"} xl:flex w-full xl:w-80 shrink-0 flex-col border-r border-gray-200 dark:border-white/10`}>
              <CalendarPanel
                allNotes={allNotes}
                loading={allNotesLoading}
                selectedDay={selectedDay}
                onSelectDay={(day) => {
                  setSelectedDay(day);
                  setSelection(null);
                  setMobileView("editor");
                }}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
              />
            </div>

            {/* Right: day notes or editor */}
            <div className={`${mobileView === "list" ? "hidden" : "flex"} xl:flex flex-1 flex-col overflow-hidden`}>
              {selection ? (
                renderEditor()
              ) : selectedDay ? (
                /* Day panel */
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        onClick={() => setMobileView("list")}
                        className="xl:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 capitalize truncate">
                        {new Date(`${selectedDay}T12:00:00`).toLocaleDateString(i18n.language, {
                          weekday: "long", day: "numeric", month: "long", year: "numeric",
                        })}
                      </span>
                    </div>
                    <button
                      onClick={() => selectNew(selectedDay ?? undefined)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 shrink-0 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t("portal.notes.new")}
                    </button>
                  </div>

                  {dayNotes.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-content-tertiary py-12">
                      <FileText className="w-8 h-8 opacity-30" />
                      <p className="text-sm">{t("portal.notes.noNotesThisDay")}</p>
                      <Button size="sm" variant="outline" onClick={() => selectNew(selectedDay ?? undefined)} startIcon={<Plus className="w-3.5 h-3.5" />}>
                        {t("portal.notes.new")}
                      </Button>
                    </div>
                  ) : (
                    <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
                      {dayNotes.map((note) => {
                        const title = getNoteTitle(note) || t("portal.notes.untitled");
                        return (
                          <li key={note.id}>
                            <button
                              onClick={() => selectNote(note)}
                              className="w-full text-left px-6 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group"
                            >
                              <div className="flex items-center gap-1.5 min-w-0">
                                {note.shared_with_therapist && (
                                  <Share2 className="w-3 h-3 text-brand-400 shrink-0" />
                                )}
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                  {title}
                                </p>
                              </div>
                              <div className="flex items-center justify-between mt-0.5 gap-2">
                                <span className="text-xs text-content-tertiary">
                                  {new Date(note.created_at).toLocaleTimeString(i18n.language, {
                                    hour: "2-digit", minute: "2-digit",
                                  })}
                                </span>
                                {note.shared_with_therapist && (
                                  note.read_by_therapist_at
                                    ? <span className="text-xs text-green-600 dark:text-green-400 font-medium">{t("portal.notes.seen")}</span>
                                    : <span className="text-xs text-brand-500 font-medium">{t("portal.notes.notSeen")}</span>
                                )}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : (
                /* No day selected */
                <div className="flex-1 hidden xl:flex flex-col items-center justify-center gap-2 text-content-tertiary">
                  <CalendarDays className="w-8 h-8 opacity-30" />
                  <p className="text-sm">{t("portal.notes.selectDay")}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
